import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { attachDisplayUrl } from './heicDisplay';
import { createPortal } from 'react-dom';
import {
    IconCloudUpload, IconCrop, IconUpload, IconTrash, IconArrowsExchange, IconCopy,
    IconZoomIn, IconZoomOut, IconRefresh, IconMaximize, IconMinimize, IconRotateClockwise,
} from '@tabler/icons-react';
import Hint from '../../../../../components/common/Hint';

/** Top bar plus the canvas padding that frames the page. */
const PAGE_CHROME_PX = 45 + 64;

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 0.15;

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const round2 = (v) => Math.round(v * 100) / 100;

/* ── One card face: dashed dropzone when empty, image + hover actions when filled ── */
function CardSlot({
    slot, url, cardW, cardH, radius, filter, iScale, title, sub, formatsPill,
    dragging, onPick, onDrop, onDragOver, onDragLeave, onCrop, onRemove, onRotate,
    cropLabel, changeLabel, removeLabel, rotateLabel,
    rotation = 0, slotRotation = 0, busy = false, busyLabel,
}) {
    const [hover, setHover] = useState(false);
    const [natural, setNatural] = useState(null);
    const interactive = Boolean(onPick);

    // A new photo has its own aspect; drop the old one so the fit is never stale.
    useEffect(() => { setNatural(null); }, [url]);

    // Auto corner detection is running for this slot.
    const busyOverlay = busy ? (
        <div
            data-html2canvas-ignore="true"
            className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-white/85 backdrop-blur-[1px] dark:bg-zinc-900/85"
            style={{ borderRadius: radius }}
        >
            <span
                className="animate-spin rounded-full border-2 border-primary/25 border-t-primary"
                style={{ width: 22 * iScale, height: 22 * iScale }}
            />
            <span className="font-semibold text-primary" style={{ fontSize: 10.5 * iScale }}>{busyLabel}</span>
        </div>
    ) : null;
    // The card box (cardW×cardH) is already the page-rotation footprint. `slotRotation`
    // spins only this face on top of that, so the frame the image is drawn in follows the
    // TOTAL turn: perpendicular when the sum lands on 90/270, and spun to fill the box.
    const totalRotation = ((rotation + slotRotation) % 360 + 360) % 360;
    const swap = totalRotation === 90 || totalRotation === 270;
    const frameW = swap ? cardH : cardW;
    const frameH = swap ? cardW : cardH;
    // The element is the photo itself, sized to fit the frame — the same shape the engine
    // embeds, so rounded corners land on the photo and no white block frames the card.
    const ir = natural ? natural.w / natural.h : frameW / frameH;
    const imgW = ir > frameW / frameH ? frameW : frameH * ir;
    const imgH = ir > frameW / frameH ? frameW / ir : frameH;

    if (!url) {
        return (
            <div
                role={interactive ? 'button' : undefined}
                tabIndex={interactive ? 0 : undefined}
                onClick={interactive ? onPick : undefined}
                onKeyDown={interactive ? (e) => e.key === 'Enter' && onPick() : undefined}
                onDrop={interactive ? onDrop : undefined}
                onDragOver={interactive ? onDragOver : undefined}
                onDragLeave={interactive ? onDragLeave : undefined}
                style={{ width: cardW, height: cardH, borderRadius: radius }}
                className={`relative flex flex-col items-center justify-center gap-1.5 border-2 border-dashed transition-colors ${interactive ? 'cursor-pointer' : ''} ${
                    dragging
                        ? 'border-primary bg-[#FFF4E6]'
                        : 'border-primary/70 bg-white hover:bg-[#FFF4E6]'
                }`}
            >
                {busyOverlay}
                <span
                    className="flex items-center justify-center rounded-full bg-[#FFF4E6]"
                    style={{ width: 40 * iScale, height: 40 * iScale }}
                >
                    <IconCloudUpload size={20 * iScale} className="text-primary" stroke={2} />
                </span>
                <span className="font-bold text-[#1B1B1F]" style={{ fontSize: 11 * iScale }}>{title}</span>
                <span className="text-[#9A9AA3]" style={{ fontSize: 9 * iScale }}>{sub}</span>
                <span
                    className="rounded font-semibold text-primary bg-[#FFF4E6]"
                    style={{ fontSize: 9 * iScale, padding: `${2 * iScale}px ${8 * iScale}px` }}
                >
                    {formatsPill}
                </span>
            </div>
        );
    }

    return (
        <div
            style={{ width: cardW, height: cardH, borderRadius: radius }}
            className="relative overflow-hidden"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            {busyOverlay}
            <img
                src={url}
                alt={title}
                draggable={false}
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: imgW,
                    height: imgH,
                    // A 90/270 turn lays the photo out wider than its card box, and the
                    // global `img { max-width: 100% }` preflight would squash it back —
                    // the box is only the footprint AFTER the rotation.
                    maxWidth: 'none',
                    maxHeight: 'none',
                    transform: `translate(-50%, -50%)${totalRotation ? ` rotate(${totalRotation}deg)` : ''}`,
                    borderRadius: radius,
                    filter,
                }}
                onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
            />
            {/* Hover action bar — only on the editable (copy 1) slots */}
            {interactive && (
            <div
                data-html2canvas-ignore="true"
                className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1.5 pb-1.5 pt-6 transition-opacity"
                style={{
                    opacity: hover ? 1 : 0,
                    pointerEvents: hover ? 'auto' : 'none',
                    background: 'linear-gradient(transparent, rgba(15,15,20,0.88))',
                }}
            >
                <Hint label={cropLabel}>
                    <button
                        type="button"
                        onClick={onCrop}
                        className="flex items-center justify-center rounded-md bg-white/85 text-[#1B1B1F] transition-transform hover:scale-105"
                        style={{ width: 26 * iScale, height: 26 * iScale }}
                    >
                        <IconCrop size={14 * iScale} stroke={1.9} />
                    </button>
                </Hint>
                <Hint label={changeLabel}>
                    <button
                        type="button"
                        onClick={onPick}
                        className="flex items-center justify-center rounded-md bg-primary text-white transition-transform hover:scale-105"
                        style={{ width: 26 * iScale, height: 26 * iScale }}
                    >
                        <IconUpload size={14 * iScale} stroke={1.9} />
                    </button>
                </Hint>
                {onRotate && (
                <Hint label={rotateLabel}>
                    <button
                        type="button"
                        onClick={onRotate}
                        className="flex items-center justify-center rounded-md text-white transition-transform hover:scale-105"
                        style={{ width: 26 * iScale, height: 26 * iScale, background: 'rgba(37,99,235,0.92)' }}
                    >
                        <IconRotateClockwise size={14 * iScale} stroke={1.9} />
                    </button>
                </Hint>
                )}
                <Hint label={removeLabel}>
                    <button
                        type="button"
                        onClick={onRemove}
                        className="flex items-center justify-center rounded-md text-white transition-transform hover:scale-105"
                        style={{ width: 26 * iScale, height: 26 * iScale, background: 'rgba(229,62,62,0.92)' }}
                    >
                        <IconTrash size={14 * iScale} stroke={1.9} />
                    </button>
                </Hint>
            </div>
            )}
        </div>
    );
}

/* ── The two-card pair (front + back), placed on the page from the engine's mm rects ── */
function NidPair({
    frontUrl, backUrl, rect, pxPerMm, radius, filter, iScale, slots, rotation,
    canSwap, onSwap, swapLabel, canDuplicate, onDuplicate, duplicateLabel,
    canRotate, onRotate, rotateLabel,
}) {
    const showCluster = canSwap || canDuplicate || canRotate;
    // The pair's own box, so the action cluster can sit dead centre of the two cards.
    const minX = Math.min(rect.front.x, rect.back.x);
    const minY = Math.min(rect.front.y, rect.back.y);
    const maxX = Math.max(rect.front.x + rect.front.w, rect.back.x + rect.back.w);
    const maxY = Math.max(rect.front.y + rect.front.h, rect.back.y + rect.back.h);
    const place = (r) => ({
        position: 'absolute',
        left: (r.x - minX) * pxPerMm,
        top: (r.y - minY) * pxPerMm,
    });

    return (
        <div
            className="absolute"
            style={{
                left: minX * pxPerMm,
                top: minY * pxPerMm,
                width: (maxX - minX) * pxPerMm,
                height: (maxY - minY) * pxPerMm,
            }}
        >
            <div style={place(rect.front)}>
                <CardSlot slot="front" url={frontUrl} cardW={rect.front.w * pxPerMm} cardH={rect.front.h * pxPerMm} radius={radius} filter={filter} iScale={iScale} rotation={rotation} {...slots.front} />
            </div>
            <div style={place(rect.back)}>
                <CardSlot slot="back" url={backUrl} cardW={rect.back.w * pxPerMm} cardH={rect.back.h * pxPerMm} radius={radius} filter={filter} iScale={iScale} rotation={rotation} {...slots.back} />
            </div>

            {showCluster && (
                <div
                    data-html2canvas-ignore="true"
                    className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center rounded-full shadow-lg"
                    style={{ gap: 5 * iScale, padding: 4 * iScale, background: 'rgba(18,18,22,0.88)', backdropFilter: 'blur(4px)' }}
                >
                    {canSwap && (
                        <Hint label={swapLabel}>
                            <button
                                type="button"
                                onClick={onSwap}
                                className="flex items-center justify-center rounded-full text-white transition-transform hover:scale-110"
                                style={{ width: 30 * iScale, height: 30 * iScale, background: 'rgba(124,58,237,0.95)' }}
                            >
                                <IconArrowsExchange size={16 * iScale} stroke={2} />
                            </button>
                        </Hint>
                    )}
                    {canDuplicate && (
                        <Hint label={duplicateLabel}>
                            <button
                                type="button"
                                onClick={onDuplicate}
                                className="flex items-center justify-center rounded-full text-white transition-transform hover:scale-110"
                                style={{ width: 30 * iScale, height: 30 * iScale, background: 'rgba(16,163,74,0.95)' }}
                            >
                                <IconCopy size={16 * iScale} stroke={2} />
                            </button>
                        </Hint>
                    )}
                    {canRotate && (
                        <Hint label={rotateLabel}>
                            <button
                                type="button"
                                onClick={onRotate}
                                className="flex items-center justify-center rounded-full text-white transition-transform hover:scale-110"
                                style={{ width: 30 * iScale, height: 30 * iScale, background: 'rgba(37,99,235,0.95)' }}
                            >
                                <IconRotateClockwise size={16 * iScale} stroke={2} />
                            </button>
                        </Hint>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * Live A4 page preview whose two card slots ARE the uploaders: photos are
 * dropped, cropped, swapped and turned inside the page they will print on.
 *
 * Everything card-shaped comes in as a prop, so the same component serves an
 * NID card (85.6x54 mm) and a passport page (88x125 mm) without either tool
 * knowing about the other's geometry.
 */
export default function DocumentCardLivePreview({
    frontFile, backFile, settings, draggingSlot, busySlot,
    onPick, onDrop, onDragOver, onDragLeave, onCrop, onRemove, onSwap, onDuplicate, onRotate,
    onRotateSlot,
    t,
    // ── Geometry of the document this page carries ──
    cardMm,
    previewPortrait,
    previewLandscape,
    maxCopies,
    computeCopyPairRects,
    i18nRoot,
    /** Optional line under the sheet, e.g. "or press Ctrl+V to paste". */
    pasteHint,
}) {
    const orientation = settings.orientation;
    const dims = orientation === 'landscape' ? previewLandscape : previewPortrait;

    const [frontUrl, setFrontUrl] = useState(null);
    const [backUrl, setBackUrl] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [fitScale, setFitScale] = useState(1);
    const [fullscreen, setFullscreen] = useState(false);
    const containerRef = useRef(null);

    // Object URLs for the uploaded files.
    useEffect(() => {
        if (!frontFile) { setFrontUrl(null); return undefined; }
        // HEIC phone photos are decoded first — see shared/heicDisplay.
        return attachDisplayUrl(frontFile, setFrontUrl);
    }, [frontFile]);

    useEffect(() => {
        if (!backFile) { setBackUrl(null); return undefined; }
        return attachDisplayUrl(backFile, setBackUrl);
    }, [backFile]);

    // Fit-to-width (and fit-to-height in fullscreen).
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;
        const measure = () => {
            const w = el.clientWidth - 4;
            let s = Math.min(1, w / dims.w);
            if (fullscreen) s = Math.min(s, (window.innerHeight * 0.9) / dims.h);
            setFitScale(s > 0 ? s : 1);
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        window.addEventListener('resize', measure);
        return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
    }, [dims.w, dims.h, fullscreen]);

    // Reset zoom whenever structure changes.
    useEffect(() => { setZoom(1); }, [orientation, settings.layout, settings.copies, settings.vPosition, settings.rotation]);

    const scale = fitScale * zoom;
    const iScale = clamp(1 / Math.max(scale, 0.28), 1, 2.05);

    const filter = useMemo(() => {
        const b = settings.brightness ?? 100;
        const c = settings.contrast ?? 100;
        return b === 100 && c === 100 ? undefined : `brightness(${b}%) contrast(${c}%)`;
    }, [settings.brightness, settings.contrast]);

    // Rotation (0/90/180/270) applied to BOTH cards. 90/270 swap the card footprint.
    const rotation = ((settings.rotation || 0) % 360 + 360) % 360;
    const rotSwap = rotation === 90 || rotation === 270;
    const copies = settings.copies || 1;

    // Card placement comes from the PDF engine, in mm, so this preview and the exported
    // page are the same layout — same margins, same gap, same shrink-to-fit for copies.
    const pxPerMm = dims.w / (orientation === 'landscape' ? 297 : 210);
    const pairRects = useMemo(
        () => computeCopyPairRects(settings.layout, orientation, settings.vPosition, copies, rotation),
        [settings.layout, orientation, settings.vPosition, copies, rotation],
    );

    // The engine clips each card canvas at a fixed radius and then scales that canvas into
    // its rect, so the on-page radius shrinks with the card — mirror that here.
    const fullCardWmm = rotSwap ? cardMm.h : cardMm.w;
    const radius = settings.roundedCorners
        ? 14 * ((pairRects[0]?.front.w ?? fullCardWmm) / fullCardWmm)
        : 0;
    const canSwap = Boolean(frontUrl && backUrl);
    const canDuplicate = Boolean(frontUrl && backUrl && onDuplicate && copies < maxCopies(rotation));

    const slotProps = {
        front: {
            title: t(`${i18nRoot}.frontTitle`), sub: t(`${i18nRoot}.frontHint`), formatsPill: t(`${i18nRoot}.formatsPill`),
            dragging: draggingSlot === 'front',
            onPick: () => onPick('front'), onDrop: (e) => onDrop('front', e),
            onDragOver: (e) => onDragOver('front', e), onDragLeave,
            onCrop: () => onCrop('front'), onRemove: () => onRemove('front'),
            onRotate: onRotateSlot ? () => onRotateSlot('front') : undefined,
            slotRotation: settings.rotationFront || 0,
            cropLabel: t(`${i18nRoot}.crop.button`), changeLabel: t(`${i18nRoot}.changeImage`), removeLabel: t(`${i18nRoot}.removeImage`),
            rotateLabel: t(`${i18nRoot}.rotateSide`),
            busy: busySlot === 'front', busyLabel: t(`${i18nRoot}.autoCrop.detecting`),
        },
        back: {
            title: t(`${i18nRoot}.backTitle`), sub: t(`${i18nRoot}.backHint`), formatsPill: t(`${i18nRoot}.formatsPill`),
            dragging: draggingSlot === 'back',
            onPick: () => onPick('back'), onDrop: (e) => onDrop('back', e),
            onDragOver: (e) => onDragOver('back', e), onDragLeave,
            onCrop: () => onCrop('back'), onRemove: () => onRemove('back'),
            onRotate: onRotateSlot ? () => onRotateSlot('back') : undefined,
            slotRotation: settings.rotationBack || 0,
            cropLabel: t(`${i18nRoot}.crop.button`), changeLabel: t(`${i18nRoot}.changeImage`), removeLabel: t(`${i18nRoot}.removeImage`),
            rotateLabel: t(`${i18nRoot}.rotateSide`),
            busy: busySlot === 'back', busyLabel: t(`${i18nRoot}.autoCrop.detecting`),
        },
    };

    // Only copy 1 is interactive/editable; extra copies mirror it visually.
    const mirrorSlots = {
        front: { ...slotProps.front, onPick: undefined, onCrop: undefined, onRemove: undefined, onRotate: undefined, busy: false },
        back: { ...slotProps.back, onPick: undefined, onCrop: undefined, onRemove: undefined, onRotate: undefined, busy: false },
    };

    const pairs = pairRects.map((rect, i) => (
        <NidPair
            key={i}
            frontUrl={frontUrl}
            backUrl={backUrl}
            rect={rect}
            pxPerMm={pxPerMm}
            radius={radius}
            filter={filter}
            iScale={iScale}
            rotation={rotation}
            slots={i === 0 ? slotProps : mirrorSlots}
            canSwap={i === 0 && canSwap}
            onSwap={onSwap}
            swapLabel={t(`${i18nRoot}.swapSides`)}
            canDuplicate={i === 0 && canDuplicate}
            onDuplicate={onDuplicate}
            duplicateLabel={t(`${i18nRoot}.duplicate`)}
            canRotate={i === 0 && Boolean((frontUrl || backUrl) && onRotate)}
            onRotate={onRotate}
            rotateLabel={t(`${i18nRoot}.rotate90`)}
        />
    ));

    const page = (
        <div
            className="bg-white"
            style={{
                width: dims.w,
                height: dims.h,
                borderRadius: 4,
                boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                position: 'relative',
            }}
        >
            {pairs}
        </div>
    );

    const zoomOut = () => setZoom((z) => round2(clamp(z - ZOOM_STEP, ZOOM_MIN, ZOOM_MAX)));
    const zoomIn = () => setZoom((z) => round2(clamp(z + ZOOM_STEP, ZOOM_MIN, ZOOM_MAX)));
    const zoomReset = () => setZoom(1);

    const topBar = (
        <div className={`flex items-center justify-between gap-2 border-b px-3.5 py-2.5 ${fullscreen ? 'border-white/10' : 'border-[#ECE8E4] dark:border-zinc-800'}`}>
            <div className="flex min-w-0 items-baseline gap-2">
                <span className={`text-[13px] font-semibold ${fullscreen ? 'text-white' : 'text-[#1B1B1F] dark:text-zinc-100'}`}>
                    {t(`${i18nRoot}.previewLive`)}
                </span>
                <span className={`truncate text-[12px] tabular-nums ${fullscreen ? 'text-white/50' : 'text-[#9A9AA3] dark:text-zinc-500'}`}>
                    {t(`${i18nRoot}.dimensions`, { w: dims.w, h: dims.h })}
                </span>
            </div>
            <div className="flex flex-none items-center gap-1">
                <span className={`mr-1 w-11 text-center text-[12px] font-semibold tabular-nums ${fullscreen ? 'text-white/70' : 'text-[#6B635C] dark:text-zinc-400'}`}>
                    {Math.round(zoom * 100)}%
                </span>
                <PvBtn onClick={zoomOut} title={t(`${i18nRoot}.zoomOut`)} disabled={zoom <= ZOOM_MIN} dark={fullscreen}><IconZoomOut size={16} /></PvBtn>
                <PvBtn onClick={zoomReset} title={t(`${i18nRoot}.zoomReset`)} disabled={zoom === 1} dark={fullscreen}><IconRefresh size={15} /></PvBtn>
                <PvBtn onClick={zoomIn} title={t(`${i18nRoot}.zoomIn`)} disabled={zoom >= ZOOM_MAX} dark={fullscreen}><IconZoomIn size={16} /></PvBtn>
                <span className={`mx-0.5 h-5 w-px ${fullscreen ? 'bg-white/15' : 'bg-[#ECE8E4] dark:bg-zinc-700'}`} />
                <PvBtn onClick={() => setFullscreen((v) => !v)} title={fullscreen ? t(`${i18nRoot}.exitFullscreen`) : t(`${i18nRoot}.fullscreen`)} dark={fullscreen}>
                    {fullscreen ? <IconMinimize size={16} /> : <IconMaximize size={16} />}
                </PvBtn>
            </div>
        </div>
    );

    const canvas = (
        <div
            ref={containerRef}
            className="flex-1 overflow-auto"
            style={{
                minHeight: 0,
                padding: 32,
                display: 'grid',
                placeItems: 'start center',
                background: fullscreen ? '#18181B' : '#F7F7F9',
                backgroundImage: 'radial-gradient(circle, rgba(120,120,130,0.28) 1px, transparent 1px)',
                backgroundSize: '14px 14px',
            }}
        >
            <div style={{ width: dims.w * scale, height: dims.h * scale, position: 'relative' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, width: dims.w, height: dims.h, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
                    {page}
                </div>
            </div>
        </div>
    );

    const shell = (
        <div className={`flex h-full min-h-0 flex-col overflow-hidden rounded-[18px] border ${fullscreen ? 'border-transparent bg-[#18181B]' : 'border-[#E8E8EE] bg-white dark:border-zinc-700 dark:bg-zinc-900'}`}>
            {topBar}
            {canvas}
            {pasteHint && !fullscreen ? (
                <div className="flex-none border-t border-[#ECE8E4] px-3.5 py-2 text-center text-[11.5px] text-[#9A9AA3] dark:border-zinc-800 dark:text-zinc-500">
                    {pasteHint}
                </div>
            ) : null}
        </div>
    );

    if (fullscreen) {
        return createPortal(
            <div className="fixed inset-0 z-[9999] bg-[#18181B]">{shell}</div>,
            document.body,
        );
    }

    // The panel is as tall as the page it is showing (plus chrome), capped at the
    // old floor. A landscape A4 is barely half the height of a portrait one, and
    // a fixed floor left that difference as dotted dead space under the page.
    const shellMinHeight = Math.min(680, Math.round(dims.h * scale) + PAGE_CHROME_PX);

    // Not `flex-1`: the column can be much taller than the sheet (a long settings
    // list, a landscape page), and stretching to fill it only adds dotted space
    // under the page.
    return <div className="flex flex-col" style={{ minHeight: shellMinHeight }}>{shell}</div>;
}

function PvBtn({ onClick, title, disabled, dark, children }) {
    return (
        <Hint label={title}>
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                aria-label={title}
                className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors disabled:opacity-35 ${
                    dark
                        ? 'bg-white/10 text-white hover:bg-white/20'
                        : 'bg-[#F7F7F9] text-[#3D3833] hover:bg-[#ECEAE7] dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700'
                }`}
            >
                {children}
            </button>
        </Hint>
    );
}
